import { type ComponentProps } from "react";
import {
  BarChart3,
  MessageSquare,
  Kanban,
  Megaphone,
  Workflow,
  Settings2,
  Search,
  ChevronsUpDown,
  Check,
  ArrowRight,
  X,
  LogOut,
  GitFork,
  Clock,
  Shield,
  Tag,
  Users,
  Sliders,
  Share2,
  Box,
  Store,
  FileText,
  PenTool,
  TrendingUp,
  Send,
  type LucideProps,
} from "lucide-react";

export interface NavIconProps extends LucideProps {
  isActive?: boolean;
}

/**
 * Clean, minimal enterprise ORAI mark:
 * Sleek precision geometric mark with pure enterprise proportions.
 */
export function OraiBrandIcon({ size = 18, className, ...props }: ComponentProps<"svg"> & { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
      {...props}
    >
      <polygon points="12 2 22 8.5 22 15.5 12 22 2 15.5 2 8.5 12 2" />
      <circle cx="12" cy="12" r="3" fill="currentColor" stroke="none" />
    </svg>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Primary Rail Navigation Icons (Minimal, crisp 1.75px stroke Lucide vectors)
// ─────────────────────────────────────────────────────────────────────────────

export function InsightsNavIcon({ size = 18, strokeWidth = 1.75, ...props }: NavIconProps) {
  return <BarChart3 size={size} strokeWidth={strokeWidth} {...props} />;
}

export function ConversationsNavIcon({ size = 18, strokeWidth = 1.75, ...props }: NavIconProps) {
  return <MessageSquare size={size} strokeWidth={strokeWidth} {...props} />;
}

export function RevenueNavIcon({ size = 18, strokeWidth = 1.75, ...props }: NavIconProps) {
  return <Kanban size={size} strokeWidth={strokeWidth} {...props} />;
}

export function SocialNavIcon({ size = 18, strokeWidth = 1.75, ...props }: NavIconProps) {
  return <Megaphone size={size} strokeWidth={strokeWidth} {...props} />;
}

export function AutomationNavIcon({ size = 18, strokeWidth = 1.75, ...props }: NavIconProps) {
  return <Workflow size={size} strokeWidth={strokeWidth} {...props} />;
}

export function WorkspaceNavIcon({ size = 18, strokeWidth = 1.75, ...props }: NavIconProps) {
  return <Settings2 size={size} strokeWidth={strokeWidth} {...props} />;
}

// ─────────────────────────────────────────────────────────────────────────────
// Shell & Sub-Navigation Utilities
// ─────────────────────────────────────────────────────────────────────────────

export function SearchIcon({ size = 15, strokeWidth = 1.75, ...props }: NavIconProps) {
  return <Search size={size} strokeWidth={strokeWidth} {...props} />;
}

export function ChevronUpDownIcon({ size = 15, strokeWidth = 1.75, ...props }: NavIconProps) {
  return <ChevronsUpDown size={size} strokeWidth={strokeWidth} {...props} />;
}

export function CheckIcon({ size = 15, strokeWidth = 2, ...props }: NavIconProps) {
  return <Check size={size} strokeWidth={strokeWidth} {...props} />;
}

export function ArrowRightIcon({ size = 15, strokeWidth = 1.75, ...props }: NavIconProps) {
  return <ArrowRight size={size} strokeWidth={strokeWidth} {...props} />;
}

export function CloseIcon({ size = 15, strokeWidth = 1.75, ...props }: NavIconProps) {
  return <X size={size} strokeWidth={strokeWidth} {...props} />;
}

export function LogoutIcon({ size = 15, strokeWidth = 1.75, ...props }: NavIconProps) {
  return <LogOut size={size} strokeWidth={strokeWidth} {...props} />;
}

// ─────────────────────────────────────────────────────────────────────────────
// Sub-Navigation Specific Icons
// ─────────────────────────────────────────────────────────────────────────────

export function StudioIcon({ size = 15, strokeWidth = 1.75, ...props }: NavIconProps) {
  return <Workflow size={size} strokeWidth={strokeWidth} {...props} />;
}

export function FlowsIcon({ size = 15, strokeWidth = 1.75, ...props }: NavIconProps) {
  return <GitFork size={size} strokeWidth={strokeWidth} {...props} />;
}

export function HistoryIcon({ size = 15, strokeWidth = 1.75, ...props }: NavIconProps) {
  return <Clock size={size} strokeWidth={strokeWidth} {...props} />;
}

export function RulesIcon({ size = 15, strokeWidth = 1.75, ...props }: NavIconProps) {
  return <Shield size={size} strokeWidth={strokeWidth} {...props} />;
}

export function CustomFieldsIcon({ size = 15, strokeWidth = 1.75, ...props }: NavIconProps) {
  return <Tag size={size} strokeWidth={strokeWidth} {...props} />;
}

export function TeamIcon({ size = 15, strokeWidth = 1.75, ...props }: NavIconProps) {
  return <Users size={size} strokeWidth={strokeWidth} {...props} />;
}

export function PreferencesIcon({ size = 15, strokeWidth = 1.75, ...props }: NavIconProps) {
  return <Sliders size={size} strokeWidth={strokeWidth} {...props} />;
}

export function IntegrationsIcon({ size = 15, strokeWidth = 1.75, ...props }: NavIconProps) {
  return <Share2 size={size} strokeWidth={strokeWidth} {...props} />;
}

export function CatalogIcon({ size = 15, strokeWidth = 1.75, ...props }: NavIconProps) {
  return <Box size={size} strokeWidth={strokeWidth} {...props} />;
}

export function CommerceIcon({ size = 15, strokeWidth = 1.75, ...props }: NavIconProps) {
  return <Store size={size} strokeWidth={strokeWidth} {...props} />;
}

export function FormsIcon({ size = 15, strokeWidth = 1.75, ...props }: NavIconProps) {
  return <FileText size={size} strokeWidth={strokeWidth} {...props} />;
}

export function ComposerIcon({ size = 15, strokeWidth = 1.75, ...props }: NavIconProps) {
  return <PenTool size={size} strokeWidth={strokeWidth} {...props} />;
}

export function EngagementIcon({ size = 15, strokeWidth = 1.75, ...props }: NavIconProps) {
  return <TrendingUp size={size} strokeWidth={strokeWidth} {...props} />;
}

export function SendIcon({ size = 15, strokeWidth = 1.75, ...props }: NavIconProps) {
  return <Send size={size} strokeWidth={strokeWidth} {...props} />;
}
